// ─────────────────────────────────────────────────────────────────────────────
// ChatWindow – Main Chat UI Panel (ESG-only, polished)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useRef, useEffect, useState, KeyboardEvent } from "react";
import type { Message, PageContext } from "../types";
import MessageBubble from "./MessageBubble";
import SuggestedPrompts from "./SuggestedPrompts";
import TransparencyPanel from "./TransparencyPanel";

interface ChatWindowProps {
	messages: Message[];
	isLoading: boolean;
	error: string | null;
	context: PageContext | null;
	isContextLoading: boolean;
	onSendMessage: (text: string) => void;
	onStopGeneration: () => void;
	onClearMessages: () => void;
	onRefreshContext: () => void;
	title: string;
	placeholder: string;
	onClose?: () => void;
}

export default function ChatWindow({
	messages,
	isLoading,
	error,
	context,
	isContextLoading,
	onSendMessage,
	onStopGeneration,
	onClearMessages,
	onRefreshContext,
	title,
	placeholder,
	onClose,
}: ChatWindowProps) {
	const [inputValue, setInputValue] = useState("");
	const [showSuggestions, setShowSuggestions] = useState(true);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	useEffect(() => {
		if (messages.length > 0) setShowSuggestions(false);
	}, [messages.length]);

	const prevContextSig = useRef<string>("");
	useEffect(() => {
		const sig = context?.esg?.domain ?? "none";
		if (sig !== prevContextSig.current && prevContextSig.current !== "") {
			setShowSuggestions(true);
		}
		prevContextSig.current = sig;
	}, [context?.esg?.domain]);

	const handleSend = () => {
		const text = inputValue.trim();
		if (!text || isLoading) return;
		setInputValue("");
		setShowSuggestions(false);
		onSendMessage(text);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleSelectPrompt = (prompt: string) => {
		setInputValue(prompt);
		setShowSuggestions(false);
		inputRef.current?.focus();
	};

	const widgetCount = context?.widgetIndex.length ?? 0;
	const hasESGContext = context?.esg != null;
	const hasContext =
		hasESGContext || widgetCount > 0 || (context?.dom.tables.length ?? 0) > 0;

	const contextLine = (() => {
		if (isContextLoading) return "Reading page context…";
		if (!hasContext) return "Ready to assist";
		const parts: string[] = [];
		if (widgetCount > 0)
			parts.push(`${widgetCount} widget${widgetCount === 1 ? "" : "s"}`);
		if (context!.esg)
			parts.push(context!.esg.domain.replace(/_/g, " "));
		if (
			parts.length === 0 &&
			(context!.dom.tables.length ?? 0) > 0
		)
			parts.push(`${context!.dom.tables.length} tables`);
		return parts.join(" · ");
	})();

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				background: "#fff",
				fontFamily:
					'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
			}}
		>
			{/* ── Header ───────────────────────────────────────────────────── */}
			<div
				className="cai-header-gradient"
				style={{
					display: "flex",
					alignItems: "center",
					padding: "12px 14px",
					color: "#fff",
					gap: 10,
					flexShrink: 0,
				}}
			>
				<div
					style={{
						width: 32,
						height: 32,
						borderRadius: 9,
						background: "rgba(255,255,255,0.18)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: 18,
						boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)",
					}}
				>
					✦
				</div>
				<div style={{ flex: 1, minWidth: 0 }}>
					<div style={{ fontWeight: 700, fontSize: 14, letterSpacing: "-0.01em" }}>
						{title}
					</div>
					<div
						style={{
							fontSize: 11,
							opacity: 0.85,
							display: "flex",
							alignItems: "center",
							gap: 6,
							marginTop: 1,
						}}
					>
						<span
							style={{
								width: 6,
								height: 6,
								borderRadius: "50%",
								background: hasContext
									? "#34D399"
									: isContextLoading
									? "#FBBF24"
									: "#94a3b8",
								boxShadow: hasContext ? "0 0 6px #34D399" : "none",
							}}
						/>
						<span
							className={isContextLoading ? "cai-loading-pulse" : ""}
							style={{
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{contextLine}
						</span>
					</div>
				</div>

				<div style={{ display: "flex", gap: 4 }}>
					<HeaderButton
						title="Refresh page context"
						onClick={onRefreshContext}
						disabled={isContextLoading}
					>
						↻
					</HeaderButton>
					<HeaderButton
						title="Clear conversation"
						onClick={onClearMessages}
						disabled={isLoading}
					>
						⌫
					</HeaderButton>
					{onClose && (
						<HeaderButton title="Close" onClick={onClose}>
							✕
						</HeaderButton>
					)}
				</div>
			</div>

			{/* ── Suggestions ──────────────────────────────────────────────── */}
			{showSuggestions && context && context.suggestedPrompts.length > 0 && (
				<SuggestedPrompts
					prompts={context.suggestedPrompts}
					esgContext={context.esg}
					entity={context.esg?.entity}
					period={context.esg?.period}
					onSelectPrompt={handleSelectPrompt}
					isVisible={showSuggestions}
				/>
			)}

			{/* ── Message list ─────────────────────────────────────────────── */}
			<div
				className="cai-scroll"
				style={{
					flex: 1,
					overflowY: "auto",
					padding: "16px 14px",
					display: "flex",
					flexDirection: "column",
					background: "#FCFBFE",
				}}
			>
				{messages.length === 0 ? (
					<EmptyState
						hasContext={hasContext}
						isContextLoading={isContextLoading}
						context={context}
					/>
				) : (
					messages.map((message) => (
						<MessageBubble key={message.id} message={message} />
					))
				)}

				{error && (
					<div
						style={{
							padding: "10px 14px",
							background: "#FEF2F2",
							border: "1px solid #FECACA",
							borderRadius: 10,
							color: "#B91C1C",
							fontSize: 12,
							marginBottom: 8,
							display: "flex",
							alignItems: "flex-start",
							gap: 8,
						}}
					>
						<span style={{ fontSize: 14 }}>⚠</span>
						<div>
							<strong>Error:</strong> {error}
						</div>
					</div>
				)}

				<div ref={messagesEndRef} />
			</div>

			{/* ── Transparency panel — only after we have a conversation ─── */}
			{messages.length > 0 && context && (
				<TransparencyPanel context={context} />
			)}

			{/* ── Input area ───────────────────────────────────────────────── */}
			<div
				style={{
					borderTop: "1px solid #EEE7F6",
					padding: "10px 12px 12px",
					background: "#fff",
					flexShrink: 0,
				}}
			>
				{hasContext && (
					<div
						style={{
							display: "flex",
							gap: 5,
							marginBottom: 8,
							flexWrap: "wrap",
						}}
					>
						<QuickButton
							highlight
							onClick={() =>
								onSendMessage(
									"Run analyze_dashboard now. Then drill into the top 2-3 most important signals using read_chart_data so you can quote real numbers, and use forecast_series on any clear trend that's relevant to ESG targets. Present the result as: (1) headline takeaway, (2) top signals with widget names and numbers, (3) recommended next actions."
								)
							}
						>
							✨ Auto-Insights
						</QuickButton>
						<QuickButton
							onClick={() =>
								onSendMessage(
									"Pick the most relevant numeric time series on this dashboard and project it 4 periods forward using forecast_series. Explain whether it's on track to hit any disclosed ESG target. Show the historical trend and the forecast band."
								)
							}
						>
							📈 Forecast
						</QuickButton>
						<QuickButton
							onClick={() =>
								onSendMessage(
									"Summarize the key data on this page and highlight the most important findings."
								)
							}
						>
							📊 Summarize
						</QuickButton>
						{hasESGContext && (
							<>
								<QuickButton
									onClick={() =>
										onSendMessage(
											"Run a materiality assessment on the ESG topics shown here. Identify financial-material vs impact-material issues."
										)
									}
								>
									🎯 Materiality
								</QuickButton>
								<QuickButton
									onClick={() =>
										onSendMessage(
											"Analyze ESG metrics against GRI, TCFD, CSRD/ESRS, IFRS S2 requirements. Identify gaps."
										)
									}
								>
									🌱 ESG Gaps
								</QuickButton>
								<QuickButton
									onClick={() =>
										onSendMessage(
											"Break down Scope 1, Scope 2, and Scope 3 emissions visible in this dashboard. Flag any methodology concerns."
										)
									}
								>
									🌍 Scope 1/2/3
								</QuickButton>
								<QuickButton
									onClick={() =>
										onSendMessage(
											"Assess CSRD/ESRS readiness based on what's disclosed here. What's missing for E1-E5, S1-S4, G1?"
										)
									}
								>
									📑 CSRD readiness
								</QuickButton>
								<QuickButton
									onClick={() =>
										onSendMessage(
											"Check BRSR alignment for the metrics on this dashboard. Map to BRSR Principles 1–9."
										)
									}
								>
									🇮🇳 BRSR
								</QuickButton>
							</>
						)}
					</div>
				)}

				<div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
					<textarea
						ref={inputRef}
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder={placeholder}
						disabled={isLoading}
						rows={2}
						style={{
							flex: 1,
							padding: "10px 12px",
							border: "1px solid #E5DEF2",
							borderRadius: 12,
							fontSize: 13,
							resize: "none",
							outline: "none",
							fontFamily: "inherit",
							lineHeight: 1.5,
							background: isLoading ? "#FAFAFB" : "#FFFFFF",
							color: "#1E1730",
							transition: "border-color 0.15s, box-shadow 0.15s",
						}}
						onFocus={(e) => {
							e.target.style.borderColor = "#482A7A";
							e.target.style.boxShadow = "0 0 0 3px rgba(72, 42, 122, 0.10)";
						}}
						onBlur={(e) => {
							e.target.style.borderColor = "#E5DEF2";
							e.target.style.boxShadow = "none";
						}}
					/>

					{isLoading ? (
						<button
							onClick={onStopGeneration}
							title="Stop generation"
							className="cai-send-btn"
							style={sendButtonStyle("#EF4444")}
						>
							■
						</button>
					) : (
						<button
							onClick={handleSend}
							disabled={!inputValue.trim()}
							title="Send (Enter)"
							className="cai-send-btn"
							style={sendButtonStyle(
								inputValue.trim()
									? "linear-gradient(135deg, #482A7A, #B31F7E)"
									: "#D1D5DB"
							)}
						>
							↑
						</button>
					)}
				</div>

				<div
					style={{
						marginTop: 6,
						fontSize: 10,
						color: "#9CA3AF",
						textAlign: "center",
					}}
				>
					Enter to send · Shift+Enter for new line
				</div>
			</div>
		</div>
	);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function sendButtonStyle(bg: string): React.CSSProperties {
	return {
		width: 42,
		height: 42,
		borderRadius: 12,
		background: bg,
		border: "none",
		color: "#fff",
		fontSize: 18,
		cursor: "pointer",
		flexShrink: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		boxShadow: "0 3px 10px rgba(72, 42, 122, 0.18)",
	};
}

function HeaderButton({
	children,
	onClick,
	disabled,
	title,
}: {
	children: React.ReactNode;
	onClick: () => void;
	disabled?: boolean;
	title?: string;
}) {
	return (
		<button
			onClick={onClick}
			disabled={disabled}
			title={title}
			className="cai-header-btn"
			style={{
				width: 30,
				height: 30,
				borderRadius: 8,
				background: "rgba(255,255,255,0.15)",
				border: "none",
				color: "#fff",
				fontSize: 13,
				cursor: disabled ? "not-allowed" : "pointer",
				opacity: disabled ? 0.5 : 1,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			{children}
		</button>
	);
}

function QuickButton({
	children,
	onClick,
	highlight,
}: {
	children: React.ReactNode;
	onClick: () => void;
	highlight?: boolean;
}) {
	const baseBg = highlight
		? "linear-gradient(135deg, #482A7A, #B31F7E)"
		: "#F5F3F8";
	const baseColor = highlight ? "#fff" : "#482A7A";
	const baseBorder = highlight ? "transparent" : "#E5DEF2";

	return (
		<button
			onClick={onClick}
			className="cai-chip"
			style={{
				padding: "5px 10px",
				background: baseBg,
				border: `1px solid ${baseBorder}`,
				borderRadius: 999,
				fontSize: 11,
				color: baseColor,
				cursor: "pointer",
				fontFamily: "inherit",
				whiteSpace: "nowrap",
				fontWeight: highlight ? 600 : 500,
				boxShadow: highlight ? "0 4px 10px rgba(72, 42, 122, 0.22)" : "none",
			}}
			onMouseEnter={(e) => {
				const el = e.currentTarget as HTMLButtonElement;
				if (highlight) {
					el.style.boxShadow = "0 6px 14px rgba(72, 42, 122, 0.32)";
				} else {
					el.style.background = "linear-gradient(135deg, #482A7A, #B31F7E)";
					el.style.color = "#fff";
					el.style.borderColor = "transparent";
				}
			}}
			onMouseLeave={(e) => {
				const el = e.currentTarget as HTMLButtonElement;
				if (highlight) {
					el.style.boxShadow = "0 4px 10px rgba(72, 42, 122, 0.22)";
				} else {
					el.style.background = "#F5F3F8";
					el.style.color = "#482A7A";
					el.style.borderColor = "#E5DEF2";
				}
			}}
		>
			{children}
		</button>
	);
}

function EmptyState({
	hasContext,
	isContextLoading,
	context,
}: {
	hasContext: boolean;
	isContextLoading: boolean;
	context: PageContext | null;
}) {
	const tags = (() => {
		if (!context?.esg) return ["GRI", "TCFD", "Scope 1/2/3", "BRSR"];
		return ["GRI 305", "TCFD", "Scope 1/2/3", "ESRS"];
	})();

	return (
		<div
			style={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: 12,
				padding: 24,
				textAlign: "center",
			}}
		>
			<div
				style={{
					width: 64,
					height: 64,
					borderRadius: 18,
					background:
						"linear-gradient(135deg, #482A7A 0%, #B31F7E 100%)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: 30,
					color: "#fff",
					boxShadow: "0 12px 28px rgba(72,42,122,0.28)",
				}}
			>
				✦
			</div>
			<div
				style={{
					fontSize: 14,
					fontWeight: 700,
					color: "#1E1730",
					letterSpacing: "-0.01em",
				}}
			>
				{isContextLoading
					? "Reading the page…"
					: hasContext
					? "Ready when you are"
					: "ESG analysis at your fingertips"}
			</div>
			<div
				style={{
					fontSize: 12,
					lineHeight: 1.55,
					maxWidth: 260,
					color: "#64748b",
				}}
			>
				{isContextLoading
					? ""
					: hasContext
					? "Pick a suggestion below or ask anything about the data on this dashboard."
					: "Ask about emissions, frameworks, materiality, or anything visible on the dashboard."}
			</div>
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					gap: 5,
					justifyContent: "center",
					marginTop: 4,
				}}
			>
				{tags.map((tag) => (
					<span
						key={tag}
						style={{
							padding: "3px 10px",
							background: "#F5F3F8",
							border: "1px solid #E5DEF2",
							borderRadius: 999,
							fontSize: 10.5,
							color: "#482A7A",
							fontWeight: 600,
						}}
					>
						{tag}
					</span>
				))}
			</div>
		</div>
	);
}
